'use client'

import ArrowForward from '@/app/_components/Icon/ArrowForward'
import { toast } from '@/app/_primitives/Toast'
import { cn } from '@/app/utils'
import { useI18n } from '@/locales/client'
import Link from 'next/link'

type Props = {
  type: 'user' | 'creator' | null
  setType: (type: 'user' | 'creator' | null) => void
}

export default function Step2({ type, setType }: Props) {
  const t = useI18n()
  if (type === 'user') {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center gap-32 p-16 transition-all duration-700 ease-in-out lg:gap-12 lg:p-0',
          {
            // Hidden state (when `type === null`)
            'pointer-events-none absolute translate-x-[-100%] opacity-0 lg:translate-x-10':
              type === null,
            // Visible state (when `type !== null`)
            'pointer-events-auto relative translate-x-0 opacity-100':
              type !== null,
          },
        )}>
        <ArrowForward
          className={
            'absolute left-0 top-0 hidden size-24 scale-x-[-1] cursor-pointer fill-icon-inverse lg:block'
          }
          onClick={() => setType(null)}
        />
        <p
          className={
            'font-500 h-106 text-center  text-24 text-[#FFFFFF99] lg:text-32'
          }>
          <span
            className={
              'c-h5 border-b-3 border-b-butterscotch text-text-inverse lg:c-h4'
            }>
            {t('onboarding.step2.title1')}
          </span>
          {t('onboarding.step2.title2')} <br />
          {t('onboarding.step2.title3')}
        </p>
        <div
          className={
            'flex w-360 flex-col items-center justify-center gap-8 lg:h-300 lg:w-571 lg:flex-row lg:gap-12'
          }>
          <ActionButton
            link={'/search/characters'}
            label={t('onboarding.step2.searchBtn')}
            description={t('onboarding.step2.searchBtnText')}
            type={'user'}
          />
          <ActionButton
            link={'/collabo'}
            label={t('onboarding.step2.collaboBtn')}
            description={t('onboarding.step2.collaboBtnText')}
            type={'user'}
          />
          <ActionButton
            link={'/insights'}
            label={t('onboarding.step2.insightsBtn')}
            description={t('onboarding.step2.insightsBtnText')}
            type={'user'}
          />
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-32 p-16 transition-all duration-700 ease-in-out lg:gap-12 lg:p-0',
        {
          // Hidden state (when `type === null`)
          'pointer-events-none absolute translate-x-[-100%] opacity-0 lg:translate-x-10':
            type === null,
          // Visible state (when `type !== null`)
          'pointer-events-auto relative translate-x-0 opacity-100':
            type !== null,
        },
      )}>
      <ArrowForward
        className={
          'absolute left-0 top-0 hidden size-24 scale-x-[-1] cursor-pointer fill-icon-inverse lg:block'
        }
        onClick={() => setType(null)}
      />
      <p
        className={
          'font-500 h-106 text-center  text-24 text-text-subtlest lg:text-32'
        }>
        <span
          className={
            'c-h5 border-b-3 border-b-butterscotch text-text-inverse lg:c-h4'
          }>
          {t('onboarding.step2.title1H')}
        </span>
        {t('onboarding.step2.title2')} <br />
        {t('onboarding.step2.title3')}
      </p>
      <div
        className={
          'flex w-360 flex-col items-center justify-center gap-8 lg:h-300 lg:w-571 lg:flex-row lg:gap-12'
        }>
        <ActionButton
          link={'/characters/create'}
          label={t('onboarding.step2.charBtn')}
          description={t('onboarding.step2.charBtnText')}
          showToast
        />
        <ActionButton
          link={'/collabo'}
          label={t('onboarding.step2.collaboBtnH')}
          description={t('onboarding.step2.collaboBtnHText')}
        />
        <ActionButton
          link={'/insights'}
          label={t('onboarding.step2.insightsBtnH')}
          description={t('onboarding.step2.insightsBtnHText')}
        />
      </div>
    </div>
  )
}

function ActionButton({
  label,
  description,
  link,
  type = 'creator',
  showToast = false,
}: {
  label: string
  description: any
  link: string
  type?: 'user' | 'creator'
  showToast?: boolean
}) {
  return (
    <Link
      href={link}
      onClick={() => {
        if (showToast) {
          toast.info({
            title: `로그인이 필요한 기능이에요.`,
            content: '',
          })
        }
      }}
      className={cn(
        'hover-action-licensee-card-gradient flex w-full cursor-pointer flex-col items-center justify-center gap-8 rounded-16 border-1 border-[#FFFFFF66] bg-white/45 p-24 text-white shadow-card-web lg:h-200 lg:w-192 lg:gap-20 lg:px-12 lg:py-32',
        {
          'hover-action-licensor-card-gradient': type === 'creator',
        },
      )}>
      <p className={'c-h6'}>{label}</p>
      <p className={'c-b3 text-center'}>{description}</p>
    </Link>
  )
}
